import React, { createContext, useContext, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'

const AuthContext = createContext({})
export const useAuth = () => useContext(AuthContext)

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [activeBusiness, setActiveBusiness] = useState('all')

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null)
      if (session?.user) fetchProfile(session.user.id)
      else setLoading(false)
    })
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null)
      if (session?.user) fetchProfile(session.user.id)
      else { setProfile(null); setLoading(false) }
    })
    return () => subscription.unsubscribe()
  }, [])

  const fetchProfile = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single()

      if (error) {
        console.error('Profile fetch error:', error.message)
        setProfile(null)
        setLoading(false)
        return
      }

      // Block deactivated users
      if (data && data.is_active === false) {
        await supabase.auth.signOut()
        setUser(null)
        setProfile(null)
        setLoading(false)
        return
      }

      setProfile(data)

      // Set default business context based on role
      if (data?.role === 'store_partner') {
        setActiveBusiness('pools')
      } else if (data?.business_ids?.length === 1) {
        setActiveBusiness(data.business_ids[0])
      }
    } catch (err) {
      console.error('Profile fetch failed:', err)
      setProfile(null)
    }
    setLoading(false)
  }

  const signIn = async (email, password) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    return { error }
  }

  const signOut = async () => {
    await supabase.auth.signOut()
    setUser(null)
    setProfile(null)
  }

  // Determine effective role: check business_roles first, fall back to profile.role
  var effectiveRole = profile?.role
  if (profile?.business_roles && activeBusiness && activeBusiness !== 'all') {
    var bizRole = profile.business_roles[activeBusiness]
    if (bizRole) effectiveRole = bizRole
  }

  const value = {
    user,
    profile,
    loading,
    activeBusiness,
    setActiveBusiness,
    signIn,
    signOut,
    role: effectiveRole,
    isOwner: effectiveRole === 'owner',
    isOffice: effectiveRole === 'office_staff',
    isPM: effectiveRole === 'project_manager',
    isCrewLeader: effectiveRole === 'crew_leader',
    isLaborer: effectiveRole === 'laborer',
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
